import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { estrategiasApi } from '@/lib/api/estrategias';
import { personaApi } from '@/lib/api/persona';

import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { Drawer } from '@/design-system/Drawer';
import { Info } from '@/design-system/Info';

const formatarData = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

export function EstrategiasPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { bairros } = useBairros();

  const { data: estrategias = [], isLoading } = useQuery({
    queryKey: ['estrategias'],
    queryFn: () => estrategiasApi.list(token),
    enabled: !!token,
  });

  // A completude da persona é o KPI de alinhamento; vem do mesmo endpoint da tela de Perfil
  const { data: persona } = useQuery({
    queryKey: ['persona'],
    queryFn: () => personaApi.get(token),
    enabled: !!token,
  });

  const [isNewOpen, setIsNewOpen] = useState(false);
  const [bairro, setBairro] = useState(bairros[0]?.n || '');
  const [tema, setTema] = useState('');

  const generateMutation = useMutation({
    mutationFn: (data: { bairro: string; tema: string }) => estrategiasApi.generate(data, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estrategias'] });
      toast('Estratégia gerada pelo OTO e salva no painel.');
      setIsNewOpen(false);
      setTema('');
    },
    onError: (err: any) => {
      // O backend bloqueia temas que violam o defeso eleitoral (RN-IA-01)
      toast(err.response?.data?.message || 'Falha ao gerar a estratégia.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => estrategiasApi.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estrategias'] });
      toast('Estratégia arquivada.');
    },
    onError: (err: any) => {
      toast(err.response?.data?.message || 'Erro ao arquivar a estratégia.');
    },
  });

  const handleGerar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!tema.trim()) {
      toast('Informe o tema da estratégia.');
      return;
    }
    generateMutation.mutate({ bairro, tema: tema.trim() });
  };

  const territorios = new Set(estrategias.map((e: any) => e.localidade?.bairro).filter(Boolean));
  const fontes = new Set(estrategias.flatMap((e: any) => e.fontes_usadas || []));
  const ultima = estrategias[0]?.created_at;

  return (
    <div className="page" id="p-estrategias">
      {/* Header */}
      <div className="head">
        <div>
          <h1 style={{ color: '#B26A00' }}>Estratégias</h1>
          <p>
            {/*
              proto:1589 promete que "cada estratégia guarda as fontes usadas",
              e o texto que estava aqui dizia que a geração combina "as fontes
              de conhecimento". Não combina: `estrategias.service.ts` monta o
              contexto do modelo a partir de Persona + Tenant e grava
              `fontes_usadas: []` literal — `oto.fonte` nunca é consultado. A
              tela de Fontes já declara a mesma lacuna, pela causa raiz.
            */}
            Planos de 30/60/90 dias gerados pelo OTO a partir da sua persona e dos dados do mandato,
            com diagnóstico, encaixe na persona, mensagem e riscos. O acervo de Fontes não entra na
            geração.
          </p>
        </div>
        <div className="act">
          <Link className="btn" to="/perfil">
            Calibrar persona
          </Link>
          <button className="btn pri" onClick={() => setIsNewOpen(true)}>
            Nova Estratégia
          </button>
        </div>
      </div>

      {/* 4 KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Estratégias ativas</div>
          <div className="v">{estrategias.length}</div>
          <div className="d">
            {territorios.size} {territorios.size === 1 ? 'território' : 'territórios'} cobertos
          </div>
        </div>

        <div className="card kpi">
          <div className="l">Persona aplicada</div>
          <div className="v" style={{ color: 'var(--good)' }}>
            {persona?.completude ?? 0}%
          </div>
          <div className="d">Tom {persona?.tom || '—'}</div>
        </div>

        <div className="card kpi">
          <div className="l">Fontes cruzadas</div>
          <div className="v">{fontes.size}</div>
          <div className="d">Pesquisas, jornais e dados públicos</div>
        </div>

        <div className="card kpi">
          <div className="l">Última geração</div>
          <div className="v" style={{ color: 'var(--eleg)', fontSize: 22 }}>
            {formatarData(ultima)}
          </div>
          <div className="d">Apoiada em dados do TSE e ouvidoria</div>
        </div>
      </div>

      {/* Lista de Estratégias */}
      {isLoading ? (
        <div className="empty">Carregando estratégias do OTO...</div>
      ) : estrategias.length === 0 ? (
        <div className="empty">
          Nenhuma estratégia gerada ainda. Use <b>Nova Estratégia</b> para o OTO produzir a primeira.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {estrategias.map((e: any) => (
            <div key={e.id} className="card" style={{ borderLeft: '4px solid #B26A00' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <h2 style={{ margin: 0 }}>
                  Estratégia · {e.tema}{' '}
                  <span className="chip eleg" style={{ fontSize: 11, marginLeft: 8 }}>
                    {e.localidade?.bairro || 'Sem território'}
                  </span>
                </h2>
                <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{formatarData(e.created_at)}</span>
              </div>

              <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--ink2)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>
                  <b>Diagnóstico:</b> {e.diagnostico}
                </div>
                <div>
                  <b>Encaixe com a persona:</b> {e.encaixe_persona}
                </div>
                {e.plano_30_60_90 && (
                  <div>
                    <b>Plano 30/60/90:</b>
                    <ul style={{ margin: '4px 0 0 18px' }}>
                      {['30', '60', '90'].map((p) =>
                        e.plano_30_60_90[p] ? <li key={p}>{`${p} dias: ${e.plano_30_60_90[p]}`}</li> : null,
                      )}
                    </ul>
                  </div>
                )}
                <div>
                  <b>Mensagem sugerida:</b> {e.mensagem}
                </div>
                {e.riscos?.length > 0 && (
                  <div>
                    <b>Riscos:</b>
                    <ul style={{ margin: '4px 0 0 18px' }}>
                      {e.riscos.map((r: string, i: number) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="filters" style={{ marginTop: 12 }}>
                <Link className="btn sm pri" to="/territorio">
                  📍 Ver no Território
                </Link>
                {e.fontes_usadas?.length > 0 && (
                  <Link className="btn sm" to="/fontes">
                    🔍 Ver em Fontes
                  </Link>
                )}
                <button
                  className="btn sm"
                  style={{ color: 'var(--crit)', marginLeft: 'auto' }}
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(e.id)}
                >
                  Arquivar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Drawer: Nova Estratégia */}
      {isNewOpen && (
        <form onSubmit={handleGerar}>
          <Drawer
            aberto
            titulo="Nova Estratégia · OTO"
            aoFechar={() => setIsNewOpen(false)}
            rodape={
              <>
                <button type="button" className="btn" onClick={() => setIsNewOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn pri" disabled={generateMutation.isPending}>
                  {generateMutation.isPending ? 'Gerando...' : 'Gerar Estratégia'}
                </button>
              </>
            }
          >
            <div className="field">
              <label>Território</label>
              <select className="sel" value={bairro} onChange={(ev) => setBairro(ev.target.value)}>
                {bairros.map((b) => (
                  <option key={b.id} value={b.n}>
                    {b.n}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Tema da estratégia</label>
              <input
                type="text"
                className="in"
                placeholder="Ex: Saúde básica, Iluminação pública, Mobilidade..."
                value={tema}
                onChange={(ev) => setTema(ev.target.value)}
                autoFocus
              />
            </div>

            <div className="note" style={{ fontSize: 12 }}>
              Temas que violem o defeso eleitoral são bloqueados antes da geração.
              <Info titulo="O que o filtro RN-IA-01 bloqueia">
                O filtro RN-IA-01 percorre uma lista fixa de termos de defeso eleitoral (compra de
                voto, calúnia, desinformação) no tema pedido e no texto gerado.
              </Info>
            </div>
          </Drawer>
        </form>
      )}
    </div>
  );
}
